/**
 * External-SSO session (e.g. Odoo → BridgeOS handoff).
 *
 * A tenant backend exchanges a one-time token (arriving as `?odoo_sso=...`)
 * for a session JWT + user object via POST /api/odoo-sso/exchange. The
 * session lives in localStorage and takes precedence in getAuthToken();
 * everything here is inert for tenants/users that never use SSO — the
 * regular Cognito flow is untouched.
 */

const TOKEN_KEY = 'bridgeos.sso.token';
const USER_KEY = 'bridgeos.sso.user';
const URL_PARAM = 'odoo_sso';

export interface SsoUser {
  username: string;
  attributes: { name?: string; email?: string };
  sso: true;
}

/** Decode a JWT payload without verification (mirrors the backend's trust model). */
function decodePayload(token: string): Record<string, unknown> | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/** The active SSO session token, or null. Clears an expired session as a side effect. */
export function getSsoToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  const payload = decodePayload(token);
  const exp = typeof payload?.exp === 'number' ? payload.exp : 0;
  if (exp * 1000 <= Date.now()) {
    clearSsoSession();
    return null;
  }
  return token;
}

export function getSsoUser(): SsoUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? { ...JSON.parse(raw), sso: true } : null;
  } catch {
    return null;
  }
}

export function clearSsoSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/** Pull the one-time token (and its source instance) out of the URL and strip them. */
export function consumeSsoParamFromUrl(): { token: string; source: string | null } | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get(URL_PARAM);
  if (!token) return null;
  const source = params.get(`${URL_PARAM}_src`);
  params.delete(URL_PARAM);
  params.delete(`${URL_PARAM}_src`);
  const rest = params.toString();
  const clean = window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash;
  window.history.replaceState({}, document.title, clean);
  return { token, source };
}

/** Exchange the one-time token with the tenant backend; stores the session on success. */
export async function exchangeSsoToken(oneTimeToken: string, source: string | null = null): Promise<SsoUser | null> {
  try {
    // Same base-URL resolution as api/client.ts — deployments serve the API
    // from a separate host (e.g. api.<domain>), not the static frontend host.
    const base = import.meta.env.VITE_API_BASE_URL || '';
    const resp = await fetch(`${base}/api/odoo-sso/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: oneTimeToken, ...(source ? { source } : {}) }),
    });
    if (!resp.ok) {
      console.warn('[sso] exchange rejected:', resp.status);
      return null;
    }
    const data = await resp.json();
    if (!data?.token || !data?.user) return null;
    const user: SsoUser = {
      username: data.user.username,
      attributes: { name: data.user.name, email: data.user.email },
      sso: true,
    };
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  } catch (e) {
    console.error('[sso] exchange failed:', e);
    return null;
  }
}
